import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Sparkles, Lock, TrendingUp, TrendingDown } from "lucide-react";
import api from "../lib/api";
import { CalendarAiModal } from "./CalendarAiModal";
import { useAuth } from "../context/AuthContext";

const RANGES = [
  { id: "prev_week", label: "Settimana precedente" },
  { id: "today", label: "Oggi" },
  { id: "tomorrow", label: "Domani" },
  { id: "week", label: "Questa settimana" },
];
const IMPORTANCE = [
  { id: "all", label: "Tutte" },
  { id: "mid", label: "Media+" },
  { id: "high", label: "Alta" },
];
const CURRENCIES = [
  { id: "all", label: "EUR + USD" },
  { id: "eu", label: "EUR" },
  { id: "us", label: "USD" },
];

const CUR_BADGE = {
  EUR: "bg-blue-500/15 text-blue-300",
  USD: "bg-emerald-500/15 text-emerald-300",
  JPY: "bg-rose-500/15 text-rose-300",
};
const IMPACT_DOT = { High: "bg-red-500", Medium: "bg-amber-500", Low: "bg-zinc-500", Holiday: "bg-zinc-600" };
const IMPACT_LABEL = { High: "Alta", Medium: "Media", Low: "Bassa", Holiday: "Festività" };

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
};

const chipClass = (active) =>
  `text-xs px-3 py-1.5 rounded-full border transition-colors ${
    active
      ? "bg-emerald-500 text-black border-emerald-500 font-medium"
      : "bg-transparent text-zinc-400 border-white/15 hover:border-white/30 hover:text-white"
  }`;

const FilterRow = ({ label, testid, options, value, onChange, prefix }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
    <span className="text-xs text-zinc-500 mr-1 w-20">{label}</span>
    {options.map((o) => (
      <button key={o.id} data-testid={`${prefix}-${o.id}`} onClick={() => onChange(o.id)} className={chipClass(value === o.id)}>
        {o.label}
      </button>
    ))}
  </div>
);

const parseVal = (s) => {
  if (!s) return null;
  const str = String(s).replace(",", ".");
  const m = str.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  let v = parseFloat(m[0]);
  if (/k/i.test(str)) v *= 1e3;
  else if (/m/i.test(str) && !/%/.test(str)) v *= 1e6;
  return v;
};

// For these indicators a LOWER value is better for the economy/currency.
const LOWER_BETTER = /unemployment|jobless|claims/i;

const surpriseKind = (event) => {
  const a = parseVal(event.actual);
  const f = parseVal(event.forecast);
  if (a == null || f == null) return null;
  const diff = a - f;
  if (Math.abs(diff) < 1e-9) return "neutral";
  const good = LOWER_BETTER.test(event.title || "") ? diff < 0 : diff > 0;
  return good ? "beat" : "miss";
};

const ActualCell = ({ event }) => {
  const kind = surpriseKind(event);
  let color = "text-zinc-700";
  if (event.actual) color = kind === "beat" ? "text-emerald-400" : kind === "miss" ? "text-red-400" : "text-zinc-200";
  // Released event still awaiting its published figure → show "In corso" (Gemini is fetching it).
  const pending = !event.actual && event.released;
  if (pending) {
    return (
      <span className="text-amber-400" data-testid="cal-actual" title="Dato in aggiornamento">
        Reale: <span className="font-mono italic">In corso…</span>
      </span>
    );
  }
  return (
    <span className={color} data-testid="cal-actual" title={kind === "beat" ? "Sopra le attese" : kind === "miss" ? "Sotto le attese" : ""}>
      Reale: <span className="font-mono">{event.actual || "—"}</span>
      {event.actual && kind === "beat" && <TrendingUp size={12} className="inline ml-1 -mt-0.5" />}
      {event.actual && kind === "miss" && <TrendingDown size={12} className="inline ml-1 -mt-0.5" />}
    </span>
  );
};

const CalendarEventRow = ({ event, onAi, aiLocked }) => {
  const isHigh = event.impact === "High";
  return (
  <div data-testid="calendar-event-row" className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
    <span className="text-xs text-zinc-500 w-16 shrink-0 font-mono">{event.time || "—"}</span>
    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm shrink-0 ${CUR_BADGE[event.currency] || "bg-white/10 text-zinc-300"}`}>{event.currency}</span>
    <span className={`w-2 h-2 rounded-full shrink-0 ${IMPACT_DOT[event.impact] || "bg-zinc-600"}`} title={IMPACT_LABEL[event.impact] || event.impact} />
    <span className="text-sm text-zinc-200 flex-1 min-w-0 truncate">{event.title}</span>
    <span className="hidden md:flex items-center gap-4 text-xs shrink-0">
      <ActualCell event={event} />
      {event.forecast ? <span className="text-zinc-400">Prev: <span className="text-white font-mono">{event.forecast}</span></span> : null}
      {event.previous ? <span className="text-zinc-600">Prec: <span className="text-zinc-400 font-mono">{event.previous}</span></span> : null}
    </span>
    {isHigh ? (
      <button
        data-testid="calendar-ai-btn"
        onClick={() => onAi(event)}
        className="shrink-0 flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-500/70 transition-colors"
        title={aiLocked ? "News AI (VIP)" : "Analisi AI dell'evento"}
      >
        {aiLocked ? <Lock size={12} /> : <Sparkles size={12} />} News AI
      </button>
    ) : (
      <span className="shrink-0 w-[86px]" aria-hidden="true" />
    )}
  </div>
  );
};

export const EconomicCalendar = () => {
  const [range, setRange] = useState("week");
  const [importance, setImportance] = useState("all");
  const [currency, setCurrency] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiEvent, setAiEvent] = useState(null);
  const { user } = useAuth();
  const isVip = !!(user && (user.role === "admin" || user.is_premium));

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/economic-calendar", { params: { range, currency, importance } })
      .then((r) => { if (active) setEvents(r.data.events || []); })
      .catch(() => { if (active) setEvents([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [range, importance, currency]);

  const days = useMemo(() => {
    const grouped = events.reduce((acc, e) => {
      (acc[e.date] = acc[e.date] || []).push(e);
      return acc;
    }, {});
    return Object.keys(grouped).sort().map((date) => ({ date, items: grouped[date] }));
  }, [events]);

  return (
    <section data-testid="economic-calendar" className="mt-16">
      <div className="flex items-center gap-2 mb-2">
        <CalendarDays size={18} className="text-emerald-400" />
        <h2 className="text-lg font-semibold">Calendario economico</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Le principali news macro di <span className="text-white">Zona Euro</span> (EUR) e
        <span className="text-white"> Stati Uniti</span> (USD), con dati reali. L'analisi
        <span className="text-emerald-300"> News AI</span> è disponibile sulle news ad alto impatto.
      </p>

      <div className="space-y-3 mb-6">
        <FilterRow label="Periodo" testid="calendar-range-filter" prefix="range" options={RANGES} value={range} onChange={setRange} />
        <FilterRow label="Valuta" testid="calendar-currency-filter" prefix="currency" options={CURRENCIES} value={currency} onChange={setCurrency} />
        <FilterRow label="Importanza" testid="calendar-importance-filter" prefix="importance" options={IMPORTANCE} value={importance} onChange={setImportance} />
      </div>

      <div className="rounded-lg border border-white/10 bg-[#141414] overflow-hidden" data-testid="calendar-events">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Caricamento eventi…</div>
        ) : days.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="calendar-empty">
            Nessun evento per questo periodo.
            {range === "prev_week" && <div className="text-xs text-zinc-600 mt-1">Lo storico della settimana precedente si popola man mano che l'app raccoglie i dati settimanali.</div>}
          </div>
        ) : (
          days.map(({ date, items }) => (
            <div key={date}>
              <div className="px-4 py-2 bg-[#0d0d0d] text-xs font-mono uppercase tracking-wider text-zinc-400 capitalize border-b border-white/5">
                {fmtDay(date)}
              </div>
              {items.map((e, idx) => (
                <CalendarEventRow key={e.key || idx} event={e} onAi={setAiEvent} aiLocked={!isVip} />
              ))}
            </div>
          ))
        )}
      </div>
      <p className="text-[11px] text-zinc-600 mt-2">Dati: Forex Factory. Orari in ora di Roma (Europe/Rome).</p>
      <CalendarAiModal event={aiEvent} open={!!aiEvent} onClose={() => setAiEvent(null)} locked={!isVip} />
    </section>
  );
};
